import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Marker, MarkerContent, MarkerIcon } from '@/components/ui/marker'
import { Spinner } from '@/components/ui/spinner'

const CODE_LENGTH = 6

/**
 * Second step of the "Get quotes for free" dialog: the code proves the address
 * from the step before is theirs, and clearing it signs them in. When a Cai
 * account that signs in by phone already holds the address, the code is texted
 * to that phone instead (phoneEnding).
 */
export function VerificationCodeStep({
  email,
  phoneEnding,
  isVerifying,
  isResending,
  errorMessage,
  onSubmit,
  onResend,
  onChangeEmail,
}: {
  email: string
  phoneEnding?: string | null
  isVerifying: boolean
  isResending: boolean
  errorMessage?: string
  onSubmit: (code: string) => void
  onResend: () => void
  onChangeEmail: () => void
}) {
  const [code, setCode] = useState('')

  function handleChange(value: string) {
    setCode(value)
    if (value.length === CODE_LENGTH && !isVerifying) onSubmit(value)
  }

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <DialogTitle>{phoneEnding ? 'Check your phone' : 'Check your email'}</DialogTitle>
        <DialogDescription>
          {phoneEnding
            ? `${email} already has a Cai account. We texted a ${CODE_LENGTH}-digit code to its phone ending ${phoneEnding}. Enter it to start your application.`
            : `We sent a ${CODE_LENGTH}-digit code to ${email}. Enter it to start your application.`}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center gap-4 py-2">
        <InputOTP
          maxLength={CODE_LENGTH}
          value={code}
          onChange={handleChange}
          autoComplete="one-time-code"
          autoFocus
          disabled={isVerifying}
        >
          <InputOTPGroup>
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        {errorMessage && <p className="text-destructive text-sm">{errorMessage}</p>}

        {/* A correct code builds the account and hydrates the carrier from CAB — seconds of work. */}
        {isVerifying ? (
          <Marker role="status" className="py-2">
            <MarkerIcon>
              <Spinner aria-hidden />
            </MarkerIcon>
            <MarkerContent className="thinking-shimmer thinking-shimmer-brand text-sm">
              Setting up your account…
            </MarkerContent>
          </Marker>
        ) : (
          <div className="flex flex-col items-center">
            <Button
              variant="ghost"
              className="text-sm"
              loading={isResending ? 'Sending…' : undefined}
              onClick={() => {
                setCode('')
                onResend()
              }}
            >
              Send a new code
            </Button>
            <Button variant="ghost" className="text-sm" onClick={onChangeEmail}>
              Use a different email
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
